import moment from 'moment-timezone';
import type { Recurrence } from 'api/recurrence_definitions/endpoints/types';
import { fetchFirstOccurrence, type SchedulingWindowEvaluation } from '../endpoints/fetchFirstOccurrence';
import { acceptDegraded } from 'validation';

export interface ComputeFirstOccurrenceArgs {
  /** Rules to evaluate; the earliest occurrence across them wins. Empty → a single windowed query. */
  readonly recurrences: readonly Recurrence[];
  /** Instance-level scheduling window key ("zone|name"), or "" for none. */
  readonly schedulingWindow: string;
  /** IANA timezone; falls back to the guessed local zone. */
  readonly timezone: string;
  /** ISO-like datetime string used as the reference. */
  readonly starttime: string;
}

export interface FirstOccurrenceComputation {
  /** Earliest valid occurrence across the queried rules, in `timezone`; null if none parsed. */
  readonly earliest: moment.Moment | null;
  /** Window evaluation echoed by the backend (the first result carrying one). */
  readonly windowEvaluation: SchedulingWindowEvaluation | undefined;
}

/**
 * Compute the first occurrence across one definition's rules.
 *
 * A definition may own several rules; with no rule but a window, a single
 * windowed query finds the next opening. Each rule is queried independently
 * and the EARLIEST valid result wins.
 *
 * Uses `Promise.allSettled`, not `Promise.all`: a definition can legitimately
 * mix a rule whose query fails (e.g. an exhausted `window_open` window) with
 * one that always fires, and a single rejection must not discard the survivors'
 * suggestions. Only when EVERY query rejects do we propagate — the first
 * rejection is rethrown so the caller's catch surfaces the backend's message.
 */
export async function computeFirstOccurrence({
  recurrences,
  schedulingWindow,
  timezone,
  starttime,
}: ComputeFirstOccurrenceArgs): Promise<FirstOccurrenceComputation> {
  const hasRecurrence = recurrences.length > 0;
  const hasWindow = !!schedulingWindow;

  const rulesToQuery: ReadonlyArray<Recurrence | null> = hasRecurrence ? recurrences : [null];

  const settled = await Promise.allSettled(
    rulesToQuery.map(async (rule) =>
      acceptDegraded(
        await fetchFirstOccurrence({
          starttime,
          starttime_timezone: timezone,
          recurrence: rule,
          scheduling_window: hasWindow ? schedulingWindow : undefined,
        })
      )
    )
  );

  const fulfilled = settled.filter((r) => r.status === 'fulfilled').map((r) => r.value);

  if (fulfilled.length === 0) {
    const firstRejection = settled.find((r) => r.status === 'rejected');
    throw firstRejection ? firstRejection.reason : new Error('Could not calculate first occurrence.');
  }

  const tz = timezone || moment.tz.guess();
  // The boundary guarantees a string, not a parseable datetime. Take the
  // earliest valid occurrence across the rules; if none parse, the caller
  // surfaces an error rather than letting an "Invalid date" through.
  let earliest: moment.Moment | null = null;
  for (const result of fulfilled) {
    const candidate = moment(result.first_occurrence).tz(tz);
    if (candidate.isValid() && (earliest === null || candidate.isBefore(earliest))) {
      earliest = candidate;
    }
  }

  const windowEvaluation = hasWindow ? fulfilled.find((result) => result.scheduling_window)?.scheduling_window : undefined;

  return { earliest, windowEvaluation };
}
